<template>
  <div class="page-container home-view">
    <section class="hero-panel dot-pattern blur-fade">
      <div class="hero-copy">
        <span class="pill"><Sparkles :size="14" /> MAGIC H5 CONCEPT</span>
        <h2>今天，也向专业<br />再靠近一点。</h2>
        <p>为注册安全工程师学习场景设计的桌面工作台。把题库、资料和智能工具放到更清晰的学习路径中。</p>
        <div class="hero-actions">
          <RouterLink to="/exams" class="shimmer-button">开始练习 <ArrowRight :size="17" /></RouterLink>
          <RouterLink to="/library" class="secondary-button">打开资料库</RouterLink>
        </div>
      </div>
      <div class="hero-visual" aria-hidden="true">
        <div class="orbit orbit-one"></div><div class="orbit orbit-two"></div>
        <div class="knowledge-core"><ShieldCheck :size="44" /><span>安全知识中枢</span><strong>SafetyVision</strong></div>
        <div class="floating-tag tag-one"><BookOpen :size="15" /> 历年真题</div>
        <div class="floating-tag tag-two"><ScanLine :size="15" /> 图片识题</div>
        <div class="floating-tag tag-three"><LibraryBig :size="15" /> 原创资料</div>
      </div>
    </section>

    <section class="metric-grid blur-fade delay-1">
      <MagicCard v-for="metric in metrics" :key="metric.label" class="metric-card">
        <div class="metric-icon" :class="metric.tone"><component :is="metric.icon" :size="19" /></div>
        <div><strong>{{ metric.value }}</strong><span>{{ metric.label }}</span></div>
        <small>{{ metric.note }}</small>
      </MagicCard>
    </section>

    <section class="dashboard-grid blur-fade delay-2">
      <MagicCard class="dashboard-card continue-card">
        <div class="section-heading"><div><span>CONTINUE</span><h3>继续学习</h3></div><RouterLink to="/exams">全部题库 <ArrowUpRight :size="15" /></RouterLink></div>
        <StatePanel v-if="loading" type="loading" title="正在同步题库" />
        <StatePanel v-else-if="error" type="error" title="题库暂时未连接" :message="error" action="重新加载" @action="load" />
        <div v-else class="continue-list">
          <button v-for="(exam, index) in examPreview" :key="exam._id || index" type="button" @click="$router.push('/exams')">
            <div class="subject-index">{{ String(index + 1).padStart(2, '0') }}</div>
            <div><strong>{{ examName(exam) }}</strong><span>进入题库选择练习方式</span></div>
            <div class="mini-progress"><i :style="{ width: `${24 + index * 13}%` }"></i></div>
            <ChevronRight :size="17" />
          </button>
        </div>
      </MagicCard>

      <MagicCard class="dashboard-card capability-card">
        <div class="section-heading"><div><span>AI CAPABILITIES</span><h3>智能能力</h3></div><span class="status-badge">接口就绪</span></div>
        <div class="capability-list">
          <RouterLink to="/tools"><div class="capability-icon purple"><ScanLine :size="20" /></div><div><strong>图片识题</strong><span>图片 / PDF 智能解析</span></div><ArrowUpRight :size="16" /></RouterLink>
          <RouterLink to="/tools"><div class="capability-icon blue"><Image :size="20" /></div><div><strong>AI 知识图解</strong><span>把抽象考点变成图解</span></div><ArrowUpRight :size="16" /></RouterLink>
          <RouterLink to="/tools"><div class="capability-icon cyan"><BrainCircuit :size="20" /></div><div><strong>智能组卷</strong><span>按知识薄弱项精准练习</span></div><ArrowUpRight :size="16" /></RouterLink>
        </div>
      </MagicCard>

      <MagicCard class="dashboard-card insight-card">
        <div class="section-heading"><div><span>THIS WEEK</span><h3>本周节奏</h3></div><span class="week-total">5 天</span></div>
        <div class="activity-bars"><div v-for="(bar, i) in bars" :key="i"><i :style="{ height: `${bar}%` }"></i><span>{{ week[i] }}</span></div></div>
        <div class="insight-note"><Flame :size="18" /><div><strong>保持连续学习</strong><span>每天 20 分钟，比突击更有效。</span></div></div>
      </MagicCard>
    </section>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { ArrowRight, ArrowUpRight, BookOpen, BrainCircuit, ChevronRight, CircleCheckBig, Flame, Image, LibraryBig, ScanLine, ShieldCheck, Sparkles, Trophy } from "lucide-vue-next";
import MagicCard from "../components/MagicCard.vue";
import StatePanel from "../components/StatePanel.vue";
import { api } from "../services/api";

const exams = ref([]); const loading = ref(true); const error = ref("");
const examPreview = computed(() => exams.value.slice(0, 3));
const metrics = [
  { value: "46", label: "可用题库", note: "持续更新", icon: BookOpen, tone: "purple" },
  { value: "94", label: "原创图页", note: "资料库", icon: LibraryBig, tone: "blue" },
  { value: "5", label: "本周学习", note: "连续天数", icon: Trophy, tone: "amber" },
  { value: "100%", label: "权益同步", note: "服务端校验", icon: CircleCheckBig, tone: "cyan" },
];
const bars = [45, 68, 38, 82, 64, 28, 72]; const week = ["一", "二", "三", "四", "五", "六", "日"];
function examName(item) { return item?.examName || item?.subjectName || item?.name || item?.title || "注册安全工程师题库"; }
async function load() {
  loading.value = true; error.value = "";
  try { const result = await api.exams(); exams.value = Array.isArray(result?.data) ? result.data : []; }
  catch (e) { error.value = e.message; }
  finally { loading.value = false; }
}
onMounted(load);
</script>
